import React, { useState } from "react";
import {
    User,
    Clock,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Edit3,
    Trash2,
    Medal,
} from "lucide-react";

const EventLeaderboardUI = ({
    runs,
    ownerData,
    editMode = false,
    selectedRuns = new Set(),
    onRunSelect,
    onEditRun,
    onDeleteRun,
    getTimeDifference,
}) => {
    const [expandedRiders, setExpandedRiders] = useState({});

    const toggleRiderExpand = (riderId) => {
        setExpandedRiders((prev) => ({ ...prev, [riderId]: !prev[riderId] }));
    };

    const handleRunSelect = (runId) => {
        onRunSelect?.(runId);
    };

    const handleEditClick = (run) => {
        onEditRun?.(run);
    };

    const handleDeleteClick = (runId) => {
        if (window.confirm("¿Eliminar esta carrera?")) {
            onDeleteRun?.(runId);
        }
    };

    const formatTimeDifference = (differenceInMs) => {
        if (!differenceInMs || differenceInMs < 0) return null;
        const ms = differenceInMs % 1000;
        const seconds = Math.floor((differenceInMs / 1000) % 60);
        const minutes = Math.floor((differenceInMs / (1000 * 60)) % 60);
        return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s ${ms}ms`;
    };

    const getMedalClass = (position) => {
        switch (position) {
            case 1:
                return "bg-gradient-to-r from-yellow-400 to-orange-500 text-yellow-900";
            case 2:
                return "bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800";
            case 3:
                return "bg-gradient-to-r from-orange-600 to-brown-700 text-orange-100";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="space-y-4">
            {runs.length === 0 ? (
                <div className="text-center py-12">
                    <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-500">
                        {editMode ? "No hay carreras para editar" : "No hay resultados"}
                    </h3>
                </div>
            ) : (
                <div className="space-y-3">
                    {runs.map((run, index) => {
                        const position = index + 1;
                        const isSelected = selectedRuns.has(run.id);
                        const owner = ownerData[run.NFCIdentifier] || {};
                        const isExpanded = expandedRiders[run.NFCIdentifier];
                        const timeDifference =
                            position > 1 && !editMode && getTimeDifference
                                ? getTimeDifference(
                                    runs[index - 1].ElapsedTime,
                                    run.ElapsedTime
                                )
                                : null;

                        return (
                            <div
                                key={run.id}
                                className={`
                rounded-xl p-4 shadow-sm transition-all duration-200
                ${editMode
                                        ? isSelected
                                            ? "border-2 border-blue-500 bg-blue-50"
                                            : "border-2 border-amber-300 bg-amber-50 hover:bg-amber-100"
                                        : position <= 3
                                            ? getMedalClass(position)
                                            : "bg-white border border-gray-200 hover:shadow-md"
                                    }
                ${editMode ? "cursor-pointer" : ""}
              `}
                                onClick={editMode ? () => handleRunSelect(run.id) : undefined}
                            >
                                {/* Edit Mode Quick Select */}
                                {editMode && (
                                    <div className="flex items-center gap-3 mb-3 p-2 bg-white rounded-lg">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleRunSelect(run.id)}
                                            className="w-4 h-4 text-blue-600 rounded"
                                        />
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900">
                                                {owner.Owner || "Desconocido"}
                                            </p>
                                            <p className="text-sm text-gray-500">{run.ElapsedTime}</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditClick(run);
                                                }}
                                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClick(run.id);
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Main Leaderboard Item */}
                                <div className="flex items-center gap-4">
                                    {!editMode && (
                                        <div
                                            className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${getMedalClass(
                                                position
                                            )}`}
                                        >
                                            {position <= 3 ? <Medal className="w-6 h-6" /> : position}
                                        </div>
                                    )}

                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="font-bold text-lg text-gray-900 truncate">
                                                {editMode
                                                    ? `${owner.Owner || "Desconocido"} (${run.NFCIdentifier
                                                    })`
                                                    : owner.Owner || "Desconocido"}
                                            </h3>
                                            {editMode && (
                                                <span
                                                    className={`px-3 py-1 rounded-full text-xs font-medium ${isSelected
                                                            ? "bg-blue-100 text-blue-800"
                                                            : "bg-amber-100 text-amber-800"
                                                        }`}
                                                >
                                                    {isSelected ? "SEL" : "EDIT"}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-3 mb-2">
                                            <Clock className="w-5 h-5 text-gray-500 flex-shrink-0" />
                                            <div className="flex-1">
                                                {run.ElapsedTime === "DNF" ? (
                                                    <div className="flex items-center gap-2 text-red-600 font-semibold">
                                                        <AlertCircle className="w-4 h-4" />
                                                        DNF
                                                    </div>
                                                ) : run.ElapsedTime === "00:00:00.001" ? (
                                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                                                        Exento
                                                    </span>
                                                ) : (
                                                    <span className="text-xl font-mono font-bold text-gray-900">
                                                        {run.ElapsedTime}
                                                    </span>
                                                )}
                                                {timeDifference && !editMode && (
                                                    <span className="ml-3 bg-black text-white px-2 py-1 rounded text-xs font-mono">
                                                        +{formatTimeDifference(timeDifference)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {editMode && (
                                            <div className="text-xs text-gray-500 space-y-1">
                                                <p>Start: {run.StartTime?.substring(0, 19) || "N/A"}</p>
                                                <p>
                                                    Finish: {run.FinishTime?.substring(0, 19) || "N/A"}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {editMode ? (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditClick(run);
                                                    }}
                                                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                                    title="Editar tiempo"
                                                >
                                                    <Edit3 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(run.id);
                                                    }}
                                                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                    title="Eliminar carrera"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => toggleRiderExpand(run.NFCIdentifier)}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                {isExpanded ? (
                                                    <ChevronUp className="w-5 h-5 text-gray-500" />
                                                ) : (
                                                    <ChevronDown className="w-5 h-5 text-gray-500" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Edit Mode Details */}
                                {editMode && isSelected && (
                                    <div className="mt-3 pt-3 border-t border-gray-200 bg-white rounded-lg p-3">
                                        <h4 className="font-medium text-gray-900 mb-2">
                                            Detalles:
                                        </h4>
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="text-gray-500">NFC:</span>{" "}
                                                {run.NFCIdentifier}
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Track:</span>{" "}
                                                {run.TrackID}
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Tiempo:</span>{" "}
                                                <span className="font-mono">{run.ElapsedTime}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {editMode && (
                <div className="mt-8 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl text-center">
                    <div className="flex items-center justify-center gap-2">
                        <Edit3 className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-amber-800">
                            Modo Edición Activo - {selectedRuns.size} seleccionada
                            {selectedRuns.size !== 1 ? "s" : ""}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventLeaderboardUI;
